import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Tag,
	Search,
	Plus,
	Edit2,
	Trash2,
	CheckCircle,
	XCircle,
	Percent,
	DollarSign,
	Zap,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

interface CouponStats {
	total: number;
	active: number;
	inactive: number;
	totalRedemptions: number;
}

interface Coupon {
	_id: string;
	code: string;
	description?: string;
	discountType: "percentage" | "fixed";
	discountValue: number;
	maxRedemptions?: number;
	timesRedeemed: number;
	validFrom?: string;
	validUntil?: string;
	applicablePlans?: string[];
	isActive: boolean;
	createdAt: string;
	autoApply?: boolean;
	durationInMonths?: number | null;
	applicableBillingCycles?: ("monthly" | "yearly")[];
	stripeCouponId?: string | null;
	maxRedemptionsPerUser?: number;
	applicableOrganizations?: string[];
	fixedAmounts?: { usd?: number; aud?: number; gbp?: number; eur?: number };
}

interface CouponFormData {
	code: string;
	description: string;
	discountType: "percentage" | "fixed";
	discountValue: number;
	maxRedemptions: string;
	validFrom: string;
	validUntil: string;
	applicablePlans: string;
	autoApply: boolean;
	durationInMonths: string;
	applicableBillingCycles: string[];
	maxRedemptionsPerUser: string;
	applicableOrganizations: string;
	fixedAmounts?: { usd: number; aud: number; gbp: number; eur: number };
}

const defaultFormData: CouponFormData = {
	code: "",
	description: "",
	discountType: "percentage",
	discountValue: 0,
	maxRedemptions: "",
	validFrom: "",
	validUntil: "",
	applicablePlans: "",
	autoApply: false,
	durationInMonths: "",
	applicableBillingCycles: ["monthly", "yearly"],
	maxRedemptionsPerUser: "",
	applicableOrganizations: "",
	fixedAmounts: { usd: 0, aud: 0, gbp: 0, eur: 0 },
};

async function fetchCoupons(params: { status?: string }) {
	const token = getAccessToken();
	const queryParams = new URLSearchParams();
	if (params.status) queryParams.append("status", params.status);

	const query = queryParams.toString();
	const res = await fetch(adminApiUrl(`/coupons${query ? `?${query}` : ""}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchCouponStats(): Promise<CouponStats> {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/coupons/stats"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function createCoupon(payload: Record<string, unknown>) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/coupons"), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

async function updateCoupon(id: string, payload: Record<string, unknown>) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/coupons/${id}`), {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

async function deleteCoupon(id: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/coupons/${id}`), {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

export function CouponList() {
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
	const [formData, setFormData] = useState<CouponFormData>(defaultFormData);
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const { data: stats } = useQuery({
		queryKey: ["admin-coupon-stats"],
		queryFn: fetchCouponStats,
	});

	const { data, isLoading, error } = useQuery({
		queryKey: ["admin-coupons", statusFilter],
		queryFn: () => fetchCoupons({ status: statusFilter || undefined }),
	});

	const coupons: Coupon[] = data?.coupons || data || [];

	const createMutation = useMutation({
		mutationFn: createCoupon,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
			queryClient.invalidateQueries({ queryKey: ["admin-coupon-stats"] });
			toast({ title: "Coupon created", description: "The coupon has been created successfully." });
			closeDialog();
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
			updateCoupon(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
			queryClient.invalidateQueries({ queryKey: ["admin-coupon-stats"] });
			toast({ title: "Coupon updated", description: "The coupon has been updated successfully." });
			closeDialog();
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteCoupon,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
			queryClient.invalidateQueries({ queryKey: ["admin-coupon-stats"] });
			toast({ title: "Coupon deactivated", description: "The coupon has been deactivated." });
		},
		onError: (error: Error) => {
			toast({ title: "Error", description: error.message, variant: "destructive" });
		},
	});

	const openCreateDialog = () => {
		setEditingCoupon(null);
		setFormData(defaultFormData);
		setDialogOpen(true);
	};

	const openEditDialog = (coupon: Coupon) => {
		setEditingCoupon(coupon);
		setFormData({
			code: coupon.code,
			description: coupon.description || "",
			discountType: coupon.discountType,
			discountValue: coupon.discountValue,
			maxRedemptions: coupon.maxRedemptions?.toString() || "",
			validFrom: coupon.validFrom
				? new Date(coupon.validFrom).toISOString().split("T")[0]
				: "",
			validUntil: coupon.validUntil
				? new Date(coupon.validUntil).toISOString().split("T")[0]
				: "",
			applicablePlans: coupon.applicablePlans?.join(", ") || "",
			autoApply: coupon.autoApply || false,
			durationInMonths: coupon.durationInMonths?.toString() || "",
			applicableBillingCycles: coupon.applicableBillingCycles || ["monthly", "yearly"],
			maxRedemptionsPerUser: coupon.maxRedemptionsPerUser?.toString() || "",
			applicableOrganizations: coupon.applicableOrganizations?.join(", ") || "",
			fixedAmounts: {
				usd: coupon.fixedAmounts?.usd ?? coupon.discountValue ?? 0,
				aud: coupon.fixedAmounts?.aud ?? 0,
				gbp: coupon.fixedAmounts?.gbp ?? 0,
				eur: coupon.fixedAmounts?.eur ?? 0,
			},
		});
		setDialogOpen(true);
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setEditingCoupon(null);
		setFormData(defaultFormData);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const payload: Record<string, unknown> = {
			code: formData.code.toUpperCase(),
			description: formData.description || undefined,
			discountType: formData.discountType,
			discountValue: formData.discountValue,
			maxRedemptions: formData.maxRedemptions
				? Number(formData.maxRedemptions)
				: undefined,
			validFrom: formData.validFrom || undefined,
			validUntil: formData.validUntil || undefined,
			applicablePlans: formData.applicablePlans
				? formData.applicablePlans
						.split(",")
						.map((s) => s.trim())
						.filter(Boolean)
				: undefined,
			autoApply: formData.autoApply,
			durationInMonths: formData.durationInMonths
				? Number(formData.durationInMonths)
				: null,
			applicableBillingCycles: formData.applicableBillingCycles,
			maxRedemptionsPerUser: formData.maxRedemptionsPerUser
				? Number(formData.maxRedemptionsPerUser)
				: undefined,
			applicableOrganizations: formData.applicableOrganizations
				? formData.applicableOrganizations
						.split(",")
						.map((s) => s.trim())
						.filter(Boolean)
				: undefined,
			...(formData.discountType === "fixed" && formData.fixedAmounts
				? (() => {
					// Clean fixedAmounts: strip zero non-USD entries
					const cleanedFixedAmounts = { ...formData.fixedAmounts };
					for (const key of ["aud", "gbp", "eur"] as const) {
						if (!cleanedFixedAmounts[key] || cleanedFixedAmounts[key] === 0) {
							delete cleanedFixedAmounts[key];
						}
					}
					return {
						fixedAmounts: cleanedFixedAmounts,
						discountValue: formData.fixedAmounts.usd,
					};
				})()
				: {}),
		};

		if (editingCoupon) {
			updateMutation.mutate({ id: editingCoupon._id, data: payload });
		} else {
			createMutation.mutate(payload);
		}
	};

	const handleDelete = (coupon: Coupon) => {
		if (
			window.confirm(
				`Are you sure you want to deactivate coupon "${coupon.code}"?`,
			)
		) {
			deleteMutation.mutate(coupon._id);
		}
	};

	const filteredCoupons = search
		? coupons.filter(
				(c) =>
					c.code.toLowerCase().includes(search.toLowerCase()) ||
					c.description?.toLowerCase().includes(search.toLowerCase()),
			)
		: coupons;

	const isExpired = (coupon: Coupon) =>
		coupon.validUntil && new Date(coupon.validUntil) < new Date();

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
					<p className="text-gray-500 mt-1">
						Manage discount coupons and promotions
					</p>
				</div>
				<Button onClick={openCreateDialog}>
					<Plus className="w-4 h-4 mr-2" />
					Create Coupon
				</Button>
			</div>

			{/* Stats Cards */}
			{stats && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<Tag className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.total}</p>
									<p className="text-sm text-gray-500">Total Coupons</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-100 rounded-lg">
									<CheckCircle className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.active}</p>
									<p className="text-sm text-gray-500">Active</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-gray-100 rounded-lg">
									<XCircle className="w-5 h-5 text-gray-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.inactive}</p>
									<p className="text-sm text-gray-500">Inactive</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-purple-100 rounded-lg">
									<Percent className="w-5 h-5 text-purple-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.totalRedemptions}</p>
									<p className="text-sm text-gray-500">Total Redemptions</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Active Auto-Apply Promos */}
			{(() => {
				const autoApplyPromos = coupons.filter(
					(c: Coupon) => c.isActive && c.autoApply && !isExpired(c),
				);
				if (autoApplyPromos.length === 0) return null;
				return (
					<Card>
						<CardContent className="p-4">
							<h3 className="text-sm font-semibold text-gray-700 mb-2">
								Active Auto-Apply Promos
							</h3>
							<div className="space-y-2">
								{autoApplyPromos.map((promo: Coupon) => (
									<div
										key={promo._id}
										className="flex items-center justify-between text-sm"
									>
										<div className="flex items-center gap-2">
											<Badge className="bg-blue-100 text-blue-700 text-xs">
												{promo.code}
											</Badge>
											<span className="text-gray-600">
												{promo.discountType === "percentage"
													? `${promo.discountValue}% off`
													: `$${promo.discountValue} off`}
												{promo.durationInMonths
													? ` for ${promo.durationInMonths} months`
													: ""}
											</span>
											{promo.applicablePlans && promo.applicablePlans.length > 0 && (
												<span className="text-gray-400">
													({promo.applicablePlans.join(", ")})
												</span>
											)}
										</div>
										<span className="text-gray-400 text-xs">
											{promo.timesRedeemed} redemptions
										</span>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				);
			})()}

			{/* Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
							<Input
								placeholder="Search coupons..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-10"
							/>
						</div>
						<select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							className="px-3 py-2 border rounded-md text-sm"
						>
							<option value="">All Status</option>
							<option value="active">Active</option>
							<option value="expired">Expired</option>
						</select>
					</div>
				</CardContent>
			</Card>

			{/* Coupons Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Coupons</span>
						<span className="text-sm font-normal text-gray-500">
							{filteredCoupons.length} coupon
							{filteredCoupons.length !== 1 ? "s" : ""}
						</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : error ? (
						<div className="text-center py-12 text-red-500">
							Failed to load coupons
						</div>
					) : filteredCoupons.length === 0 ? (
						<div className="text-center py-12 text-gray-500">
							No coupons found
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full">
								<thead>
									<tr className="border-b">
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Code
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Discount
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Usage
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Valid Period
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Plans
										</th>
										<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
											Status
										</th>
										<th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">
											Actions
										</th>
									</tr>
								</thead>
								<tbody>
									{filteredCoupons.map((coupon) => (
										<tr
											key={coupon._id}
											className="border-b last:border-0 hover:bg-gray-50"
										>
											<td className="py-3 px-4">
												<div className="flex items-center gap-2">
													<Tag className="w-4 h-4 text-gray-400" />
													<span className="font-mono font-medium">
														{coupon.code}
													</span>
													{coupon.autoApply && (
														<Badge className="bg-amber-100 text-amber-700 text-xs ml-1">
															<Zap className="w-3 h-3 mr-0.5" />
															Auto
														</Badge>
													)}
													{coupon.maxRedemptionsPerUser && (
														<Badge className="bg-purple-100 text-purple-700 text-xs ml-1">
															{coupon.maxRedemptionsPerUser}x/user
														</Badge>
													)}
													{coupon.applicableOrganizations && coupon.applicableOrganizations.length > 0 && (
														<Badge className="bg-teal-100 text-teal-700 text-xs ml-1">
															{coupon.applicableOrganizations.length} org{coupon.applicableOrganizations.length > 1 ? "s" : ""}
														</Badge>
													)}
												</div>
												{coupon.description && (
													<p className="text-xs text-gray-400 mt-0.5 ml-6">
														{coupon.description}
													</p>
												)}
											</td>
											<td className="py-3 px-4">
												<div className="flex items-center gap-1">
													{coupon.discountType === "percentage" ? (
														<>
															<Percent className="w-3 h-3 text-gray-400" />
															<span className="font-medium">
																{coupon.discountValue}%
															</span>
														</>
													) : (
														<>
															<DollarSign className="w-3 h-3 text-gray-400" />
															<span className="font-medium">
																{coupon.discountValue / 100}
															</span>
														</>
													)}
												</div>
											</td>
											<td className="py-3 px-4 text-sm">
												<span>
													{coupon.timesRedeemed}
													{coupon.maxRedemptions
														? ` / ${coupon.maxRedemptions}`
														: ""}
												</span>
												{coupon.maxRedemptions && (
													<div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
														<div
															className="h-full bg-blue-500 rounded-full"
															style={{
																width: `${Math.min(100, (coupon.timesRedeemed / coupon.maxRedemptions) * 100)}%`,
															}}
														/>
													</div>
												)}
											</td>
											<td className="py-3 px-4 text-sm text-gray-500">
												{coupon.validFrom || coupon.validUntil ? (
													<>
														{coupon.validFrom
															? new Date(
																	coupon.validFrom,
																).toLocaleDateString()
															: "Start"}
														{" - "}
														{coupon.validUntil
															? new Date(
																	coupon.validUntil,
																).toLocaleDateString()
															: "No end"}
													</>
												) : (
													"No limit"
												)}
											</td>
											<td className="py-3 px-4">
												{coupon.applicablePlans &&
												coupon.applicablePlans.length > 0 ? (
													<div className="flex flex-wrap gap-1">
														{coupon.applicablePlans.map((plan) => (
															<Badge
																key={plan}
																variant="outline"
																className="text-xs"
															>
																{plan}
															</Badge>
														))}
													</div>
												) : (
													<span className="text-sm text-gray-400">
														All plans
													</span>
												)}
											</td>
											<td className="py-3 px-4">
												<div className="flex items-center gap-1.5">
													{coupon.isActive && !isExpired(coupon) ? (
														<Badge className="bg-green-100 text-green-700">
															Active
														</Badge>
													) : (
														<Badge className="bg-gray-100 text-gray-700">
															{isExpired(coupon) ? "Expired" : "Inactive"}
														</Badge>
													)}
													{coupon.autoApply && (
														<Badge className="bg-blue-100 text-blue-700 text-[10px]">
															Auto
														</Badge>
													)}
												</div>
											</td>
											<td className="py-3 px-4 text-right">
												<div className="flex items-center justify-end gap-1">
													<Button
														variant="ghost"
														size="sm"
														onClick={() => openEditDialog(coupon)}
													>
														<Edit2 className="w-4 h-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleDelete(coupon)}
														disabled={deleteMutation.isPending}
													>
														<Trash2 className="w-4 h-4 text-red-500" />
													</Button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Create/Edit Dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingCoupon ? "Edit Coupon" : "Create Coupon"}
						</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Code
							</label>
							<Input
								value={formData.code}
								onChange={(e) =>
									setFormData({
										...formData,
										code: e.target.value.toUpperCase(),
									})
								}
								placeholder="e.g., SUMMER2024"
								required
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Description
							</label>
							<Input
								value={formData.description}
								onChange={(e) =>
									setFormData({ ...formData, description: e.target.value })
								}
								placeholder="Optional description"
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Discount Type
								</label>
								<select
									value={formData.discountType}
									onChange={(e) =>
										setFormData({
											...formData,
											discountType: e.target.value as "percentage" | "fixed",
										})
									}
									className="w-full px-3 py-2 border rounded-md text-sm"
								>
									<option value="percentage">Percentage</option>
									<option value="fixed">Fixed Amount</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Discount Value
								</label>
								<Input
									type="number"
									value={formData.discountValue}
									onChange={(e) =>
										setFormData({
											...formData,
											discountValue: Number(e.target.value),
										})
									}
									min={0}
									step="any"
									required
								/>
							</div>
						</div>
						{formData.discountType === "fixed" && (
							<div className="space-y-2 mt-3">
								<label className="block text-sm font-medium text-gray-700">
									Fixed Amounts by Currency
								</label>
								{([
									{ key: "usd", label: "USD ($)" },
									{ key: "aud", label: "AUD (A$)" },
									{ key: "gbp", label: "GBP (£)" },
									{ key: "eur", label: "EUR (€)" },
								] as const).map((cur) => (
									<div key={cur.key} className="flex items-center gap-2">
										<span className="text-sm text-gray-600 w-20">{cur.label}</span>
										<Input
											type="number"
											step="0.01"
											min={0}
											className="w-32"
											value={formData.fixedAmounts?.[cur.key] || ""}
											onChange={(e) => {
												const val = Number(e.target.value);
												setFormData({
													...formData,
													fixedAmounts: {
														...formData.fixedAmounts,
														usd: formData.fixedAmounts?.usd ?? 0,
														aud: formData.fixedAmounts?.aud ?? 0,
														gbp: formData.fixedAmounts?.gbp ?? 0,
														eur: formData.fixedAmounts?.eur ?? 0,
														[cur.key]: val,
													},
													...(cur.key === "usd" ? { discountValue: val } : {}),
												});
											}}
										/>
									</div>
								))}
								<p className="text-xs text-gray-400">
									USD is required. Others optional (falls back to USD).
								</p>
							</div>
						)}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Max Redemptions (optional)
							</label>
							<Input
								type="number"
								value={formData.maxRedemptions}
								onChange={(e) =>
									setFormData({ ...formData, maxRedemptions: e.target.value })
								}
								placeholder="Unlimited"
								min={1}
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Max Uses Per User (optional)
							</label>
							<Input
								type="number"
								value={formData.maxRedemptionsPerUser}
								onChange={(e) =>
									setFormData({ ...formData, maxRedemptionsPerUser: e.target.value })
								}
								placeholder="Unlimited"
								min={1}
							/>
							<p className="text-xs text-gray-500 mt-1">
								Limit how many times each user can use this coupon
							</p>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Restrict to Organizations (optional)
							</label>
							<Input
								value={formData.applicableOrganizations}
								onChange={(e) =>
									setFormData({ ...formData, applicableOrganizations: e.target.value })
								}
								placeholder="org-id-1, org-id-2"
							/>
							<p className="text-xs text-gray-500 mt-1">
								Comma-separated org IDs. Leave empty for all organizations.
							</p>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Valid From
								</label>
								<Input
									type="date"
									value={formData.validFrom}
									onChange={(e) =>
										setFormData({ ...formData, validFrom: e.target.value })
									}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Valid Until
								</label>
								<Input
									type="date"
									value={formData.validUntil}
									onChange={(e) =>
										setFormData({ ...formData, validUntil: e.target.value })
									}
								/>
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Applicable Plans (comma-separated)
							</label>
							<Input
								value={formData.applicablePlans}
								onChange={(e) =>
									setFormData({ ...formData, applicablePlans: e.target.value })
								}
								placeholder="e.g., starter, professional"
							/>
							<p className="text-xs text-gray-500 mt-1">
								Leave empty to apply to all plans
							</p>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Billing Cycles
								</label>
								<div className="flex gap-3">
									<label className="flex items-center gap-1.5 text-sm">
										<input
											type="checkbox"
											checked={formData.applicableBillingCycles.includes("monthly")}
											onChange={(e) => {
												const cycles = e.target.checked
													? [...formData.applicableBillingCycles, "monthly"]
													: formData.applicableBillingCycles.filter((c) => c !== "monthly");
												setFormData({ ...formData, applicableBillingCycles: cycles });
											}}
										/>
										Monthly
									</label>
									<label className="flex items-center gap-1.5 text-sm">
										<input
											type="checkbox"
											checked={formData.applicableBillingCycles.includes("yearly")}
											onChange={(e) => {
												const cycles = e.target.checked
													? [...formData.applicableBillingCycles, "yearly"]
													: formData.applicableBillingCycles.filter((c) => c !== "yearly");
												setFormData({ ...formData, applicableBillingCycles: cycles });
											}}
										/>
										Yearly
									</label>
								</div>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Duration
								</label>
								<input
									type="number"
									min="0"
									max="60"
									placeholder="Leave empty for one-time"
									value={formData.durationInMonths}
									onChange={(e) =>
										setFormData({ ...formData, durationInMonths: e.target.value })
									}
									className="w-full px-3 py-2 border rounded-md text-sm"
								/>
								<p className="text-xs text-gray-500 mt-1">
									Number of months (1-60). Leave empty for one-time.
								</p>
							</div>
						</div>
						<div>
							<label className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<input
									type="checkbox"
									checked={formData.autoApply}
									onChange={(e) =>
										setFormData({ ...formData, autoApply: e.target.checked })
									}
								/>
								Auto-apply (applies automatically at checkout without code)
							</label>
							{formData.autoApply && !formData.durationInMonths && (
								<p className="text-xs text-amber-600 mt-1">
									Duration is required for auto-apply coupons
								</p>
							)}
						</div>
						{/* Price Preview */}
						{formData.discountValue > 0 && formData.applicablePlans && (
							<div className="p-3 bg-gray-50 rounded-lg text-sm">
								<p className="font-medium text-gray-700 mb-1">Preview</p>
								{formData.applicablePlans
									.split(",")
									.map((p) => p.trim())
									.filter(Boolean)
									.map((planSlug) => {
										const discountLabel =
											formData.discountType === "percentage"
												? `${formData.discountValue}% off`
												: `$${formData.discountValue} off`;
										const durationLabel = formData.durationInMonths
											? `for ${formData.durationInMonths} months`
											: "one-time";
										return (
											<p key={planSlug} className="text-gray-600">
												{planSlug}: {discountLabel} {durationLabel}
											</p>
										);
									})}
							</div>
						)}
						{/* Stripe Sync Status */}
						{editingCoupon && (
							<div className="text-xs text-gray-400">
								Stripe: {editingCoupon.stripeCouponId ? (
									<span className="text-green-600">Synced</span>
								) : (
									<span className="text-amber-600">Will sync on save</span>
								)}
							</div>
						)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={closeDialog}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={
									createMutation.isPending || updateMutation.isPending
								}
							>
								{editingCoupon ? "Save Changes" : "Create Coupon"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
